import type { ReactNode } from 'react';
import { Component } from 'react';
import styled from 'styled-components';

import Paragraph from 'components/core/typography/Paragraph';
import PrimaryText from 'components/core/typography/PrimaryText';
import { ErrorBoundaryIcon } from 'components/ui/icons';
import Button from 'components/ui/shared/Buttons/Button';
import { DatadogCustomAction } from 'enums/datadogCustomAction';

import { FONT_SIZE_24, FONT_WEIGHT_BOLDEST, NEUTRAL_050, SPACE_500 } from 'styles/tokens';

import DevErrorDetails from '../errors/DevErrorDetails';
import LoggingService from '../logging/LoggingService';

const ErrorBoundaryLayout = styled.div`
  align-items: center;
  background-color: ${NEUTRAL_050};
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding-top: 100px;
  width: 100%;
`;

const SectionBlock = styled.div`
  text-align: center;
`;

const Heading = styled(PrimaryText)`
  font-size: ${FONT_SIZE_24};
  font-weight: ${FONT_WEIGHT_BOLDEST};
  margin: ${SPACE_500} 0;
  text-transform: uppercase;
`;

const RefreshButton = styled(Button)`
  margin: ${SPACE_500} auto;
  width: 140px;
`;

interface Props {
  children: ReactNode;
  scope?: string;
}

interface State {
  componentStack?: string;
  error?: Error;
  prevScope: string;
}

class ErrorBoundary extends Component<Props, State> {
  static defaultProps = {
    scope: 'error-boundary',
  };

  constructor(props) {
    super(props);
    this.state = { error: undefined, prevScope: props.scope };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    if (state.prevScope !== props.scope) {
      return {
        error: undefined,
        prevScope: props.scope,
      };
    }

    return state;
  }

  /**
   * Log the event.
   *
   * Errors caught by a React error boundary are "handled" and are NOT auto-captured by Datadog
   * (unlike uncaught runtime exceptions). So we explicitly report them:
   *  - `captureReactError` records a proper RUM error with the React component stack
   *    (via `@datadog/browser-rum-react`), so it surfaces in Datadog Error Tracking.
   *  - `trackAction(ERROR_BOUNDARY)` keeps the breadcrumb/GTM event for session context.
   *  - For expected exceptions use `LoggingService.captureException` where the exception actually occurs.
   */
  componentDidCatch(error, info) {
    LoggingService.captureReactError(error, info);

    LoggingService.trackAction(DatadogCustomAction.ERROR_BOUNDARY, {
      error,
      info,
    });

    if (process.env.NODE_ENV === 'development' && info?.componentStack) {
      this.setState({ componentStack: info.componentStack });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <ErrorBoundaryLayout>
          <ErrorBoundaryIcon />
          <SectionBlock>
            <Heading>Something went wrong!</Heading>
            <Paragraph>Looks like you&apos;ve found a bug.</Paragraph>
            <Paragraph>
              We track these errors automatically, but if the problem persists feel free to contact us.
            </Paragraph>
            <RefreshButton
              onClick={() => {
                globalThis.location.reload();
              }}
              variant="primary"
            >
              Refresh
            </RefreshButton>
          </SectionBlock>
          {process.env.NODE_ENV === 'development' && (
            <DevErrorDetails componentStack={this.state.componentStack} error={this.state.error} />
          )}
        </ErrorBoundaryLayout>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
